<script setup>
import { computed } from 'vue';
import { cva } from 'class-variance-authority';
import { useI18n } from 'vue-i18n';
import { appStoreUrl } from '@/seo/siteContent.js';

/**
 * "Download on the App Store", tagged with the campaign that shows in
 * App Store Connect, so every page's downloads can be counted.
 */
const props = defineProps({
  campaign: { type: String, required: true },
  size: { type: String, default: 'md' },
  tone: { type: String, default: 'ink' },
  block: { type: Boolean, default: false },
  subtitle: { type: Boolean, default: true },
});

const { t } = useI18n();

const button = cva(
  'group inline-flex items-center gap-3 font-semibold transition-all duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-deen-gold focus-visible:ring-offset-2',
  {
    variants: {
      tone: {
        ink: 'bg-deen-ink text-white shadow-[0_18px_40px_-12px_rgba(28,72,72,0.5)] hover:shadow-[0_22px_50px_-12px_rgba(28,72,72,0.6)]',
        gold: 'bg-deen-gold text-deen-ink shadow-[0_18px_40px_-12px_rgba(236,215,159,0.6)]',
        light: 'bg-white text-deen-ink border border-deen-ink/10 shadow-sm hover:shadow-md',
      },
      size: { sm: 'h-10 px-4 rounded-xl text-xs', md: 'h-14 px-5 rounded-2xl text-sm', lg: 'h-16 px-6 rounded-2xl text-base' },
      block: { true: 'w-full justify-center', false: '' },
    },
  }
);

const href = computed(() => appStoreUrl(props.campaign));
</script>

<template>
  <a :href="href" target="_blank" rel="noopener" :class="button({ tone, size, block })">
    <iconify-icon icon="mdi:apple" :class="size === 'sm' ? 'text-lg' : 'text-2xl'" aria-hidden="true" />
    <span class="text-left leading-tight">
      <span v-if="subtitle && size !== 'sm'" class="block text-[10px] font-semibold uppercase tracking-widest opacity-60">
        {{ t('quran.app.available') }}
      </span>
      <span class="block">{{ t('home.download.ios') }}</span>
    </span>
  </a>
</template>
